import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useEffect } from "react";

export default function PrivacyModal({ open, onClose }) {
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80] bg-ink-950/95 backdrop-blur-md overflow-y-auto"
                    data-testid="privacy-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Privacy Policy"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                        className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-24"
                    >
                        <div className="flex items-start justify-between gap-6 mb-10">
                            <div>
                                <div className="overline mb-4">
                                    <span className="text-flash">■</span>&nbsp;&nbsp;Documento legale
                                </div>
                                <h1
                                    className="font-display uppercase text-bone-50 leading-[0.85] tracking-[-0.03em]"
                                    style={{ fontSize: "clamp(2rem, 6vw, 5rem)" }}
                                >
                                    Privacy Policy<span className="text-flash">.</span>
                                </h1>
                                <p className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-bone-400">
                                    Ai sensi del Reg. UE 2016/679 (GDPR) — Ultimo aggiornamento: 11 agosto 2026
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                aria-label="Chiudi"
                                className="p-3 border border-white/15 rounded-full text-bone-50 hover:border-flash hover:text-flash transition-colors flex-shrink-0"
                                data-testid="privacy-close"
                            >
                                <X size={18} strokeWidth={1.5} />
                            </button>
                        </div>

                        <div className="space-y-8 text-bone-200 font-light text-sm md:text-base leading-relaxed">
                            <Section num="01" title="Titolare del trattamento">
                                <p>
                                    Il titolare del trattamento dei dati è <strong className="text-bone-50">Francesco Ciotoli</strong>,
                                    fotografo e videomaker con base a Ceccano (FR), Italia. Per qualsiasi richiesta relativa
                                    ai dati personali è possibile scrivere a{" "}
                                    <a href="mailto:ciotoli02@gmail.com" className="text-flash link-underline">ciotoli02@gmail.com</a>.
                                </p>
                            </Section>

                            <Section num="02" title="Dati raccolti">
                                <p>Dal modulo di contatto raccogliamo esclusivamente:</p>
                                <ul className="list-disc pl-5 mt-3 space-y-1 text-bone-400">
                                    <li>Nome</li>
                                    <li>Indirizzo email</li>
                                    <li>Tipologia di servizio richiesto</li>
                                    <li>Contenuto del messaggio</li>
                                </ul>
                                <p className="mt-3">Il conferimento è volontario ma necessario per rispondere alla richiesta.</p>
                            </Section>

                            <Section num="03" title="Finalità e base giuridica">
                                <p>
                                    I dati sono trattati per rispondere alle richieste di preventivo e informazioni,
                                    sulla base del <strong className="text-bone-50">consenso esplicito</strong> prestato al momento
                                    dell'invio del modulo (art. 6.1.a GDPR) e per l'esecuzione di misure precontrattuali
                                    su tua richiesta (art. 6.1.b GDPR).
                                </p>
                            </Section>

                            <Section num="04" title="Conservazione">
                                <p>
                                    I dati sono conservati per un massimo di <strong className="text-bone-50">24 mesi</strong>{" "}
                                    dall'ultimo contatto e successivamente cancellati, salvo obblighi di legge.
                                </p>
                            </Section>

                            <Section num="05" title="Destinatari e trasferimenti">
                                <p>
                                    I dati sono conservati su server MongoDB e inoltrati via email tramite il servizio
                                    Resend (fornitore email compliant). I server possono essere localizzati in UE o
                                    in Paesi con clausole contrattuali standard approvate dalla Commissione Europea.
                                    Non cediamo dati a terze parti a fini commerciali.
                                </p>
                            </Section>

                            <Section num="06" title="Cookie">
                                <p>
                                    Il sito utilizza cookie tecnici (essenziali per il funzionamento) e, previo consenso,
                                    cookie analitici anonimi (PostHog) per statistiche di navigazione. Nessun cookie di
                                    profilazione pubblicitaria. Puoi modificare il consenso in qualsiasi momento cancellando
                                    i dati del browser.
                                </p>
                            </Section>

                            <Section num="07" title="I tuoi diritti">
                                <p>Ai sensi degli artt. 15–22 GDPR hai diritto a:</p>
                                <ul className="list-disc pl-5 mt-3 space-y-1 text-bone-400">
                                    <li>Accedere ai tuoi dati</li>
                                    <li>Chiederne rettifica o cancellazione</li>
                                    <li>Limitarne o opporti al trattamento</li>
                                    <li>Ricevere una copia in formato portabile</li>
                                    <li>Revocare il consenso in qualsiasi momento</li>
                                    <li>Presentare reclamo al Garante Privacy (<a href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer" className="text-flash link-underline">garanteprivacy.it</a>)</li>
                                </ul>
                                <p className="mt-3">
                                    Per esercitare i tuoi diritti scrivi a{" "}
                                    <a href="mailto:ciotoli02@gmail.com" className="text-flash link-underline">ciotoli02@gmail.com</a>.
                                </p>
                            </Section>

                            <Section num="08" title="Sicurezza">
                                <p>
                                    Adottiamo misure tecniche e organizzative adeguate (HTTPS, hosting in UE dove possibile,
                                    accessi ristretti) per proteggere i tuoi dati da accessi non autorizzati o perdita.
                                </p>
                            </Section>
                        </div>

                        <div className="mt-16 pt-8 border-t border-white/10 flex items-center justify-between">
                            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-bone-400">
                                © {new Date().getFullYear()} Francesco Ciotoli
                            </span>
                            <button onClick={onClose} className="btn-flash" data-testid="privacy-close-bottom">
                                <span>Ho capito</span>
                                <span>→</span>
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function Section({ num, title, children }) {
    return (
        <section className="grid grid-cols-12 gap-4 md:gap-8 border-t border-white/10 pt-6">
            <div className="col-span-12 md:col-span-3">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-flash">{num}</div>
                <h3 className="mt-2 font-display uppercase text-bone-50 leading-tight text-lg md:text-xl tracking-[-0.02em]">
                    {title}
                </h3>
            </div>
            <div className="col-span-12 md:col-span-9">{children}</div>
        </section>
    );
}
